<template>
    <div>
        <Navigation />

        <div class="mt-5">
            <div>
                <div>
                    <ticto-loading v-if="loading" />
                    <!-- Toolbar única: sub-navegação de auditoria colapsada num
                         dropdown (ganho de largura em notebooks) + slot onde as
                         páginas filhas teleportam seus controles -->
                    <div
                        class="mb-3 flex flex-wrap items-center justify-between gap-3"
                    >
                        <div class="flex min-w-0 flex-wrap items-center gap-3">
                            <Menu as="div" class="relative">
                                <MenuButton
                                    class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-800 transition-colors hover:border-gray-300 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-indigo-300 dark:hover:border-gray-500"
                                >
                                    <component
                                        :is="activeSubnav.icon"
                                        class="h-4 w-4"
                                    />
                                    {{ activeSubnav.name }}
                                    <ChevronDownIcon
                                        class="h-3.5 w-3.5 text-gray-400"
                                    />
                                </MenuButton>
                                <transition
                                    enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="scale-95 transform opacity-0"
                                    enter-to-class="scale-100 transform opacity-100"
                                    leave-active-class="transition duration-75 ease-in"
                                    leave-from-class="scale-100 transform opacity-100"
                                    leave-to-class="scale-95 transform opacity-0"
                                >
                                    <MenuItems
                                        class="absolute left-0 z-50 mt-1.5 flex min-w-[190px] flex-col gap-0.5 rounded-xl border border-gray-200 bg-white p-1.5 shadow-[0_12px_32px_rgba(20,21,26,0.12)] focus:outline-none dark:border-gray-600 dark:bg-gray-800"
                                    >
                                        <MenuItem
                                            v-for="item in subnav"
                                            :key="item.name"
                                            v-slot="{ active }"
                                        >
                                            <router-link
                                                :to="item.route"
                                                class="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm"
                                                :class="[
                                                    isActiveSubnav(item)
                                                        ? 'bg-indigo-50 font-semibold text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300'
                                                        : 'text-gray-600 dark:text-gray-300',
                                                    active &&
                                                    !isActiveSubnav(item)
                                                        ? 'bg-gray-100 dark:bg-gray-700'
                                                        : '',
                                                ]"
                                            >
                                                <component
                                                    :is="item.icon"
                                                    class="h-4 w-4"
                                                />
                                                {{ item.name }}
                                            </router-link>
                                        </MenuItem>
                                    </MenuItems>
                                </transition>
                            </Menu>

                            <span
                                class="h-5 w-px shrink-0 bg-gray-200 dark:bg-gray-700"
                            ></span>

                            <div
                                id="audit-toolbar"
                                class="flex min-w-0 flex-wrap items-center gap-3"
                            ></div>
                        </div>

                        <div class="flex shrink-0 items-center gap-2">
                            <!-- Status de Observação -->
                            <button
                                v-if="observation"
                                type="button"
                                class="flex items-center gap-1 text-sm text-yellow-500 transition-all hover:text-yellow-600 hover:underline"
                                @click="openObservationModal"
                                v-tooltip="
                                    'Observação iniciada em ' +
                                    new Date(
                                        observation.insertedAt,
                                    ).toLocaleDateString('pt-BR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                    }) +
                                    '. Clique para detalhes.'
                                "
                            >
                                <EyeIcon class="h-5 w-5" />
                                <span class="hidden xl:inline"
                                    >Em observação</span
                                >
                            </button>

                            <!-- Status de Auditoria: discreto, mas com o estado
                                 e a data sempre visíveis -->
                            <router-link
                                v-if="audit"
                                :to="{
                                    name: 'audit.interactions',
                                    params: { audit_id: audit.id },
                                }"
                                target="_blank"
                                class="flex items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
                                v-tooltip="'Ver interações da auditoria'"
                            >
                                <span
                                    class="h-1.5 w-1.5 shrink-0 rounded-full"
                                    :class="
                                        audit.status === 'closed'
                                            ? 'bg-gray-400 dark:bg-gray-500'
                                            : 'bg-amber-500'
                                    "
                                ></span>
                                Auditoria
                                {{
                                    audit.status === "closed"
                                        ? "finalizada"
                                        : "iniciada"
                                }}
                                em {{ auditDateLabel }}
                            </router-link>

                            <!-- Botões de Ação -->
                            <observation-modal
                                v-if="!observation && observationStatusLoaded"
                                :user-id="user.id"
                                button-text="Observar"
                                @fetch="fetchObservationStatus"
                            />
                            <store-modal
                                v-if="!audit"
                                @fetch="fetchAuditStatus"
                                :user-id="user.id"
                            >
                                Auditar
                            </store-modal>
                        </div>
                    </div>

                    <!-- Modal de Observação - Para referência do modal através do ref -->
                    <observation-modal
                        v-if="observation"
                        ref="observationModalRef"
                        :user-id="user.id"
                        :observation-data="observation"
                        @fetch="fetchObservationStatus"
                        :hide-button="true"
                    />

                    <router-view v-if="!loading" :user="user"></router-view>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    UsersIcon,
    CubeIcon,
    BanknotesIcon,
    UserIcon,
    CurrencyDollarIcon,
    ChartBarIcon,
    ChevronDownIcon,
    CursorArrowRaysIcon,
    EyeIcon,
} from "@heroicons/vue/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import UserClicks from "../../../components/user/audit/UserClicks.vue";
import Navigation from "../../../components/layouts/navigation/vertical/Navigation.vue";
import TictoLoading from "../../../components/layouts/TictoLoading.vue";
import StoreModal from "../../../components/audit/modals/StoreModal.vue";
import ObservationModal from "../../../components/user/observation/ObservationModal.vue";
import { useToast } from "vue-toastification";
const toast = useToast();

export default {
    components: {
        StoreModal,
        TictoLoading,
        Navigation,
        UserClicks,
        EyeIcon,
        ChevronDownIcon,
        ObservationModal,
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
    },
    props: ["user"],

    data() {
        return {
            audit: false,
            observation: null,
            observationModalRef: null,
            observationStatusLoaded: false,
            affiliates: [],
            products: [],
            table: false,
            loading: false,
            clicks: [],
            pagination: {
                total: 0,
                per_page: 35,
                current_page: 1,
                last_page: 11,
                from: 1,
                to: 11,
            },
            aggs: {
                browser: {},
                city: {},
                ip: {},
                device: {},
            },
        };
    },

    computed: {
        /** Itens da sub-navegação de auditoria (colapsada no dropdown). */
        subnav() {
            return [
                {
                    name: "Clicks",
                    route: { name: "user.trackings" },
                    icon: CursorArrowRaysIcon,
                },
                {
                    name: "Produtos",
                    route: { name: "user.audit.products" },
                    icon: CubeIcon,
                },
                {
                    name: "Afiliados",
                    route: { name: "user.audit.affiliates" },
                    icon: UsersIcon,
                },
                {
                    name: "Vendas",
                    route: { name: "user.audit.commissions" },
                    icon: BanknotesIcon,
                },
                {
                    name: "Logins",
                    route: { name: "user.audit.logins" },
                    icon: UserIcon,
                },
                {
                    name: "Financeiro",
                    route: { name: "user.audit.finnancial" },
                    icon: CurrencyDollarIcon,
                },
                {
                    name: "Metricas",
                    route: { name: "user.metrics" },
                    icon: ChartBarIcon,
                },
            ];
        },

        activeSubnav() {
            return (
                this.subnav.find((item) => this.isActiveSubnav(item)) ??
                this.subnav[this.subnav.length - 1]
            );
        },

        auditDateLabel() {
            const raw = this.audit?.created_at;

            if (!raw) {
                return "";
            }

            const parsed = new Date(String(raw).replace(" ", "T"));

            return isNaN(parsed) ? raw : parsed.toLocaleDateString("pt-BR");
        },
    },

    mounted() {
        this.fetch();
        this.fetchObservationStatus();
    },

    methods: {
        isActiveSubnav(item) {
            return this.$route.name === item.route.name;
        },

        fetch() {
            this.fetchAuditStatus();
        },

        fetchAuditStatus() {
            const params = {};
            params["filter[user_id]"] = this.$route.params.user_id;

            axios
                .get("/api/audit", {
                    params,
                })
                .then(({ data }) => {
                    this.audit = data.data[0];
                });
        },

        fetchObservationStatus() {
            axios
                .get(`/api/user/observation/${this.user.id}`)
                .then(({ data }) => {
                    if (data.data.isUnderObservation) {
                        this.observation = data.data;
                    } else {
                        this.observation = false;
                    }
                    this.observationStatusLoaded = true;
                })
                .catch((error) => {
                    console.error(
                        "Erro ao verificar status de observação:",
                        error,
                    );
                    this.observation = false;
                    this.observationStatusLoaded = true;
                });
        },

        openObservationModal() {
            if (this.$refs.observationModalRef) {
                this.$refs.observationModalRef.openModal();
            }
        },

        UsersIcon,
        CubeIcon,
        BanknotesIcon,
        UserIcon,
        CurrencyDollarIcon,
        ChartBarIcon,
        async syncWithClickUp() {
            try {
                await axios.post(`/api/v1/audit/${route.params.id}/clickup`);
                toast.success("Task sincronizada com sucesso!");
            } catch (error) {
                toast.error("Erro ao sincronizar com ClickUp");
            }
        },
    },
};
</script>
